import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  MessageSquare, 
  FileText, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const GoogleWave = () => {
  const faqData = [
    {
      question: "What happened to Google Wave?",
      answer: "Google Wave was discontinued in August 2010 due to low user adoption. The service became read-only on January 31, 2012, and was completely shut down on April 30, 2012. Despite its innovative features, users found it confusing and couldn't identify clear use cases."
    },
    {
      question: "Is there a free alternative to Google Wave for real-time collaboration?",
      answer: "Yes! Chill Study Work offers free real-time collaboration features including video study rooms, chat, and productivity tools. While CSW focuses on study and work sessions, it provides the collaborative spirit that made Wave special."
    },
    {
      question: "What made Google Wave special?",
      answer: "Google Wave pioneered real-time collaborative editing, allowing multiple users to edit the same content simultaneously and watch each other type. It also featured a unique 'playback' function to replay conversation history and supported rich media embedding."
    },
    {
      question: "How does CSW compare to Google Wave?",
      answer: "CSW offers real-time collaboration through video study rooms and chat, plus productivity tools like Pomodoro timers and task management that Wave didn't have. While Wave focused on replacing email, CSW focuses on productive work sessions with community support."
    },
    {
      question: "Did Google Wave's technology survive?",
      answer: "Yes! Wave's real-time editing technology (operational transform) lives on in Google Docs and other collaborative editors. Many concepts Wave pioneered—threaded conversations, inline comments, real-time sync—are now standard in tools like Slack, Notion, and Microsoft Teams."
    },
    {
      question: "Can I collaborate with others on CSW like I could on Google Wave?",
      answer: "Absolutely! CSW offers live video study rooms where you can work alongside others in real-time, share progress, chat, and stay accountable. It's collaborative productivity that's more focused than Wave's open-ended approach."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Google Wave Alternative - Free Real-Time Collaboration",
    "description": "Looking for a Google Wave alternative? Google Wave shut down in 2012 but CSW offers free real-time collaboration with video study rooms, chat, and productivity tools.",
    "url": "https://chillstudy.work/google-wave/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Google Wave",
      "applicationCategory": "CollaborationApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "Google Docs",
        "url": "https://docs.google.com/"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Notion",
        "url": "https://www.notion.so/"
      }
    ],
    "keywords": "Google Wave alternative, Google Wave replacement, real-time collaboration, collaborative workspace, team communication, productivity tools"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work - Google Wave Alternative",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Real-Time Video Study Rooms",
      "Live Chat and Communication",
      "Collaborative Productivity",
      "Pomodoro Study Timer",
      "Task Management",
      "Cross-Platform Access",
      "Community Study Sessions",
      "Focus Mode"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Google Wave Alternative - Free Real-Time Collaboration | CSW</title>
        <meta name="description" content="Looking for a Google Wave alternative? Google Wave shut down in 2012 but CSW offers free real-time collaboration with video study rooms, chat, and productivity tools." />
        <meta name="keywords" content="Google Wave alternative, Google Wave replacement, real-time collaboration, collaborative workspace, productivity tools" />
        <link rel="canonical" href="https://chillstudy.work/google-wave/" />
        <meta property="og:title" content="Google Wave Alternative - Free Real-Time Collaboration" />
        <meta property="og:description" content="Google Wave shut down in 2012. Discover CSW's free real-time collaboration with video study rooms and productivity tools." />
        <meta property="og:url" content="https://chillstudy.work/google-wave/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Google Wave shut down on April 30, 2012
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              The Best Google Wave Alternative for Real-Time Collaboration
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Miss Google Wave's revolutionary collaboration features? Chill Study Work offers{" "}
              <Link to="/study-with-me/" className="text-primary hover:underline">real-time study rooms</Link> where 
              you can work alongside others, chat, and stay productive — all from your browser.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/google-wave/history/">
                  Read Google Wave's Story
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">What Happened to Google Wave?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <a href="https://en.wikipedia.org/wiki/Google_Wave" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Wave</a> was 
              an ambitious collaboration platform launched in 2009 that combined email, instant messaging, and document editing into a single real-time experience. 
              Created by <a href="https://en.wikipedia.org/wiki/Lars_Rasmussen_(software_developer)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Lars and Jens Rasmussen</a> (the 
              brothers behind Google Maps), Wave was ahead of its time.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Despite the hype, Wave's complexity confused users and it failed to gain traction. Google discontinued development in August 2010, 
              and the service was completely shut down on April 30, 2012.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              If you're looking for real-time collaboration with a clearer focus, 
              CSW offers <Link to="/group-study/" className="text-primary hover:underline">collaborative study rooms</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">productivity timers</Link>,{" "}
              <Link to="/task-management/" className="text-primary hover:underline">task management</Link>, and{" "}
              <Link to="/study-with-friends/" className="text-primary hover:underline">community features</Link>.
            </p>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Wave's Collaboration Vision, Made Simple
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Real-Time Study Rooms</h3>
                <p className="text-muted-foreground">
                  Join live video rooms and work alongside others—experience real-time presence without the complexity.
                  <Link to="/group-study/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <MessageSquare className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Live Chat</h3>
                <p className="text-muted-foreground">
                  Communicate instantly with study partners through integrated chat features.
                  <Link to="/study-with-strangers/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-green-500/10 rounded-lg w-fit mb-4">
                  <Clock className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Pomodoro Timer</h3>
                <p className="text-muted-foreground">
                  Built-in productivity timer—something Wave never had for focused work sessions.
                  <Link to="/study-timer/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-purple-500/10 rounded-lg w-fit mb-4">
                  <FileText className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Organize your work with lists and goals—clear focus that Wave lacked.
                  <Link to="/task-management/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ambient Sounds</h3>
                <p className="text-muted-foreground">
                  Focus better with lo-fi beats, rain sounds, and café ambiance while you work.
                  <Link to="/studystream/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-blue-500/10 rounded-lg w-fit mb-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Simple Interface</h3>
                <p className="text-muted-foreground">
                  Clear, focused design—no confusion about what to do or when to use it.
                  <Link to="/productivity-tool/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Wave's Legacy */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Tools That Inherited Wave's Ideas</h2>
            <p className="text-muted-foreground mb-6">
              Google Wave was ahead of its time. Many of its innovations now live on in popular collaboration tools:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="https://docs.google.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Google Docs</p>
                  <p className="text-sm text-muted-foreground">Real-time editing using Wave's technology</p>
                </div>
              </a>
              <a href="https://slack.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Slack</p>
                  <p className="text-sm text-muted-foreground">Threaded conversations and app integrations</p>
                </div>
              </a>
              <a href="https://www.notion.so/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Notion</p>
                  <p className="text-sm text-muted-foreground">Unified workspace combining docs, chat, and tasks</p>
                </div>
              </a>
              <a href="https://www.microsoft.com/en-us/microsoft-teams/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Microsoft Teams</p>
                  <p className="text-sm text-muted-foreground">Chat, collaboration, and real-time editing</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Blog Links */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Collaboration & Productivity Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/science-behind-study-with-me/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">The Science Behind Study With Me</p>
                <p className="text-sm text-muted-foreground">Why working alongside others boosts productivity</p>
              </Link>
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How Turning On My Camera Boosted Productivity</p>
                <p className="text-sm text-muted-foreground">The power of social accountability</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools for collaborative work</p>
              </Link>
              <Link to="/blog/how-to-stop-procrastination/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How to Stop Procrastination</p>
                <p className="text-sm text-muted-foreground">Science-backed strategies to get things done</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-primary/10 rounded-lg">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Frequently Asked Questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card rounded-xl border border-border/50 px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready for Real-Time Collaboration That Works?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Experience the collaborative spirit of Google Wave without the complexity — focused productivity with a community that keeps you accountable.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Start Collaborating Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Google Wave" />

      <Footer />
    </div>
  );
};

export default GoogleWave;
